import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trophy, Bell, User, LogOut, LayoutDashboard, Shield, Crown, Globe, Wallet, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useRegion, REGIONS } from "@/context/RegionContext";
import { api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function Navbar() {
  const { user, logout } = useAuth();
  const { region, setRegion, cfg } = useRegion();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [balance, setBalance] = useState(null);

  const switchRegion = (r) => {
    setRegion(r);
    navigate(REGIONS[r].home);
  };

  const fetchBalance = useCallback(async () => {
    if (!user) { setBalance(null); return; }
    try {
      const { data } = await api.get("/wallet/balance", { params: { region } });
      setBalance(Number(data.balance || 0));
    } catch { setBalance(null); }
  }, [user, region]);

  const fetchNotifs = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data.notifications);
      setUnread((prev) => {
        if (data.unread > prev && prev >= 0) {
          const latest = data.notifications.find((n) => !n.read);
          if (latest) toast.warning(latest.message);
        }
        return data.unread;
      });
    } catch {}
  }, [user]);

  useEffect(() => {
    if (!user) return;
    fetchNotifs();
    fetchBalance();
    const t = setInterval(() => { fetchNotifs(); fetchBalance(); }, 6000);
    return () => clearInterval(t);
  }, [user, fetchNotifs, fetchBalance]);

  const markRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setUnread(0);
    } catch {}
  };

  return (
    <nav className="fixed top-0 inset-x-0 z-50 glass border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to={cfg.home} data-testid="nav-logo" className="flex items-center gap-2 group">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-[0_0_20px_rgba(59,130,246,0.4)]">
            <Trophy className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-black text-lg tracking-tighter">PodiumOne</span>
        </Link>

        <div className="flex items-center gap-2 sm:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="region-switcher" className="inline-flex items-center gap-1.5 glass rounded-full px-3 py-1.5 text-sm hover:border-white/30 transition-colors">
                <span>{cfg.flag}</span>
                <span className="hidden sm:inline font-medium">{cfg.label}</span>
                <span className="text-white/40 text-xs">({cfg.currency})</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 glass-strong border-white/10 text-white">
              <div className="px-3 py-2 text-xs font-bold uppercase tracking-widest text-white/40 flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5" /> Market
              </div>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem data-testid="region-option-global" onClick={() => switchRegion("global")} className="cursor-pointer focus:bg-white/10">
                🌐 Global (USD) {region === "global" && <Check className="w-4 h-4 ml-auto text-emerald-400" />}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="region-option-my" onClick={() => switchRegion("MY")} className="cursor-pointer focus:bg-white/10">
                🇲🇾 Malaysia (MYR) {region === "MY" && <Check className="w-4 h-4 ml-auto text-emerald-400" />}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {user && (
            <button
              data-testid="nav-wallet-btn"
              onClick={() => navigate(region === "MY" ? "/my/wallet" : "/wallet")}
              className="inline-flex items-center gap-1.5 glass rounded-full px-3 py-1.5 text-sm hover:border-white/30 transition-colors"
              title="Wallet — top up"
            >
              <Wallet className="w-4 h-4 text-emerald-400" />
              <span data-testid="nav-wallet-balance" className="font-semibold tabular-nums">
                {balance === null ? "Wallet" : `${region === "MY" ? "RM " : "$"}${balance.toFixed(2)}`}
              </span>
            </button>
          )}

          {user ? (
            <>
              <DropdownMenu onOpenChange={(o) => o && markRead()}>
                <DropdownMenuTrigger asChild>
                  <button data-testid="notif-bell" className="relative p-2 rounded-full hover:bg-white/10 transition-colors">
                    <Bell className="w-5 h-5 text-white/80" />
                    {unread > 0 && (
                      <span data-testid="notif-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-[10px] font-bold flex items-center justify-center">
                        {unread}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 glass-strong border-white/10 text-white">
                  <div className="px-3 py-2 text-xs font-bold uppercase tracking-widest text-white/40">Notifications</div>
                  <DropdownMenuSeparator className="bg-white/10" />
                  {notifs.length === 0 && <div className="px-3 py-6 text-center text-sm text-white/40">No notifications yet</div>}
                  {notifs.slice(0, 8).map((n) => (
                    <div key={n.notif_id} data-testid="notif-item" className={`px-3 py-2.5 text-sm ${n.read ? "text-white/50" : "text-white"}`}>
                      <div>{n.message}</div>
                      {n.type === "outbid" && n.slug && (
                        <button
                          data-testid="reclaim-btn"
                          onClick={() => navigate(`/k/${n.slug}?bid=1`)}
                          className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-emerald-400 hover:text-emerald-300 transition-colors"
                        >
                          <Crown className="w-3.5 h-3.5" /> Reclaim #1
                        </button>
                      )}
                    </div>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu" className="w-9 h-9 rounded-full glass-strong flex items-center justify-center hover:border-white/30 transition-colors overflow-hidden">
                    {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <User className="w-4 h-4" />}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 glass-strong border-white/10 text-white">
                  <div className="px-3 py-2">
                    <div className="text-sm font-semibold truncate">{user.name}</div>
                    <div className="text-xs text-white/40 truncate">{user.email}</div>
                  </div>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")} className="cursor-pointer focus:bg-white/10">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="cursor-pointer focus:bg-white/10">
                      <Shield className="w-4 h-4 mr-2" /> Admin Portal
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="cursor-pointer focus:bg-white/10 text-red-400">
                    <LogOut className="w-4 h-4 mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Button data-testid="nav-login-btn" onClick={() => navigate("/login")} className="rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:opacity-90 active:scale-95 transition-all font-semibold">
              Sign in
            </Button>
          )}
        </div>
      </div>
    </nav>
  );
}
